import React from "react";

import { useMutation, useQuery } from "@apollo/client";
import { styled, Typography } from "@mui/material";

import { graphql } from "@/gql";
import { Package } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { displayError } from "@/utils/errors";

import CurrentSubscriptionCard from "../components/CurrentSubscriptionCard";

const Root = styled("div")(({ theme }) => ({
  // Theme spacing unit is 4px: 16px gutters, 32px of air below the card.
  padding: theme.spacing(0, 4, 8),
}));

const subscriptionPageQuery = graphql(`
  query SubscriptionPage {
    ...CurrentSubscriptionCard
  }
`);

// One-time Xendit checkout (auto-debit recurring is pending approval): each payment buys one
// package period, and the backend webhook extends the coach's access when it completes.
const createCheckoutSessionMutation = graphql(`
  mutation CreateCheckoutSession($input: CreateCheckoutSessionInput!) {
    createCheckoutSession(input: $input) {
      checkoutUrl
      errors {
        message
      }
    }
  }
`);

// Xendit has no hosted customer portal, so cancellation is in-app. The backend stops auto-debit
// immediately but keeps access until the paid period ends (subscriptionCancelAt).
const cancelSubscriptionMutation = graphql(`
  mutation CancelSubscription {
    cancelSubscription(input: {}) {
      coach {
        id
        subscriptionStatus
        subscriptionCancelAt
        autoRenewing
      }
    }
  }
`);

const SubscriptionPage: React.FC = () => {
  const { data, loading } = useQuery(subscriptionPageQuery);
  const snackbar = useGlobalSnackbar();

  const [createCheckoutSession] = useMutation(createCheckoutSessionMutation);
  const [cancelSubscription] = useMutation(cancelSubscriptionMutation);

  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(
      "Cancel your subscription? Auto-renewal stops immediately and you'll keep access until the end of your paid period."
    );
    if (!confirmed) return;

    try {
      await cancelSubscription();
      snackbar.open("Your subscription has been cancelled.");
    } catch (error) {
      snackbar.open(displayError(error));
    }
  };

  const handleCheckout = async (selectedPackage: Package) => {
    try {
      const result = await createCheckoutSession({
        variables: {
          input: {
            package: selectedPackage,
            successUrl: `${import.meta.env.VITE_APP_URL}/subscription`,
            cancelUrl: `${import.meta.env.VITE_APP_URL}/subscription`,
          },
        },
      });
      const { checkoutUrl, errors } = result.data?.createCheckoutSession ?? {};
      if (checkoutUrl) {
        window.location.replace(checkoutUrl);
      } else {
        // The mutation reports failures (e.g. a rejected Xendit session) in the payload, not as
        // a GraphQL error — surface them instead of silently doing nothing.
        snackbar.open(
          errors?.[0]?.message ?? "Could not start checkout — please try again."
        );
      }
    } catch (error) {
      snackbar.open(displayError(error));
    }
  };

  return (
    <Root>
      <Typography variant="body1" fontWeight="bold" paddingY={4}>
        My Subscription
      </Typography>
      <CurrentSubscriptionCard
        data={data}
        loading={loading}
        onCheckout={handleCheckout}
        onCancelSubscription={handleCancelSubscription}
      />
    </Root>
  );
};

export default SubscriptionPage;
